import type { FlagGuardrailVerdict, FlagKind, FlagStepGate } from 'definitions';
import { FLAG_GUARDRAIL_MIN_OFF_CONVERSIONS, FLAG_GUARDRAIL_MIN_VISITORS_PER_ARM } from 'definitions';

/*
 * Every sentence the Feature flags screens show, in one place so copy.test.ts can hold it to
 * the house rules: no em or en dashes, short. One word for each thing, on the page and in the
 * create dialog alike:
 *
 * - Off and On are the two sides. Off is the site or the code as it is today.
 * - Rollout is the share of visitors who get On.
 * - Turn off sends everyone to Off at once. It is the page's main action on a live flag.
 * - Plan is the list of steps the rollout moves through on its own; a Step is one of them.
 * - Guardrail is the check that On does no worse than Off on the primary goal.
 * - Ring is a group that always gets one side, decided before the rollout.
 * - Holdback is the share kept Off at every step.
 *
 * A pause or a guardrail breach is explained once, on the status card under the name. The
 * cards below it carry a short status chip at most.
 */
export const FLAG_COPY = {
  common: {
    unsaved: 'Unsaved changes',
  },
  list: {
    search: 'Search flags',
    create: 'Create flag',
    selectWebsite: 'Select a website first.',
    loading: 'Loading feature flags…',
    loadFailed: 'Could not load the feature flags for this website.',
    retry: 'Try again',
    noMatch: 'No flag matches the search.',
    emptyView: 'No flags in this view.',
    showAll: 'Show all flags',
    reset: 'Reset',
  },
  empty: {
    title: 'No feature flags yet',
    description:
      'A flag turns a change on for a share of your visitors, and widens that share step by step. Turn off takes it back at once.',
  },
  kind: {
    code: 'Code',
    editor: 'Editor',
  } satisfies Record<FlagKind, string>,
  create: {
    title: 'Create flag',
    // The kind, the key and the payloads are fixed once the flag exists, so this does not
    // promise, as the experiment dialog does, that all of it can change afterwards.
    intro: 'Name it, choose what it does, and set up its two sides. Nothing reaches visitors until you publish it.',
    kind: 'How it runs',
    kinds: {
      editor: { label: 'Editor', description: 'Change the site with the visual editor.' },
      code: { label: 'Code', description: 'Your code asks for it, through the SDK, the browser or HTTP.' },
    } satisfies Record<FlagKind, { label: string; description: string }>,
    name: 'Name',
    namePlaceholder: 'e.g. New checkout',
    key: 'Flag key',
    keyHelp: 'What your code asks for: lowercase letters, digits and hyphens. It cannot change later.',
    keyInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter or digit.',
    description: 'Description',
    descriptionPlaceholder: 'What is it for?',
    sides: 'Sides',
    off: 'Off',
    offChip: 'default',
    offNote: {
      editor: 'Added automatically: your site as it is today. Everyone outside the rollout gets it.',
      code: 'Added automatically: your code as it is today. Everyone outside the rollout gets it.',
    } satisfies Record<FlagKind, string>,
    on: 'On',
    onNote: {
      editor: 'The changes you make in the visual editor, for the share you roll out to.',
      code: 'Your new code path, for the share you roll out to.',
    } satisfies Record<FlagKind, string>,
    payload: 'Payload',
    payloadPlaceholder: 'Optional JSON, e.g. { "color": "blue" }',
    payloadInvalid: 'is not valid JSON.',
    bucketOn: 'Bucket on',
    bucketHelp:
      'The ID that decides the side. The same ID always gets the same side, so Customer ID keeps a signed in customer on one side on every device.',
    visitor: 'Visitor ID',
    customerId: 'Customer ID',
    failed: 'Could not create the flag.',
  },
  tabs: {
    rollout: 'Rollout',
    brief: 'Brief',
    targeting: 'Targeting',
    setup: 'Setup',
    activity: 'Activity',
  },
  header: {
    publish: 'Publish',
    publishing: 'Publishing…',
    publishTitle: 'Publish flag',
    published: 'Flag published.',
    turnOff: 'Turn off',
    turnOffTitle: 'Turn off this flag?',
    turnOffBody: 'Everyone gets Off from now on. A running plan is paused.',
    turnedOff: 'Flag turned off.',
    archive: 'Archive',
    archiveTitle: 'Archive flag',
    archiveBody: 'An archived flag serves Off to everyone and cannot be turned on again.',
    archived: 'Flag archived.',
    delete: 'Delete',
    deleteTitle: 'Delete flag',
    deleteBody: 'The draft and its settings are removed. This cannot be undone.',
    noChange: 'You do not have permission to change rollouts.',
    moreOptions: 'More options',
    loadFailed: 'Could not load this flag.',
    rename: {
      action: 'Rename flag',
      title: 'Rename flag',
      field: 'Flag name:',
      done: 'Flag renamed.',
      failed: 'Could not rename the flag.',
    },
  },
  status: {
    stages: { draft: 'Draft', live: 'Live', archived: 'Archived' },
    getOn: (percent: string) => `${percent} get On`,
    rollout: 'Rollout',
    plan: 'Plan',
    next: 'Next',
    createdBy: 'created by',
    noPlan: 'No plan',
    planStates: {
      none: 'Not started',
      active: (step: number, total: number) => `Step ${step} of ${total}`,
      paused: (step: number, total: number) => `Paused at step ${step} of ${total}`,
      done: 'Done',
    },
    notLive: 'Not live yet',
    nothingNext: 'Nothing scheduled',
    archivedNext: 'Everyone gets Off',
    waiting: 'Waiting for you',
    publishesAt: (date: string) => `Publishes ${date}`,
    turnsOffAt: (date: string) => `Turns off ${date}`,
    guardrailPaused: 'Paused by the guardrail',
    // The guardrail's own words (run-flag-guardrails.ts) come first; this stands in when there are none.
    guardrailPausedReason: 'On did worse than Off on the primary goal.',
    guardrailPausedHelp: 'Check the Guardrail card on the Rollout tab, then resume the plan or turn off.',
    planPaused: 'Plan paused',
    planPausedHelp: 'The rollout stays where it is until you resume the plan.',
  },
  rollout: {
    title: 'Rollout',
    description: 'The share of visitors who get On.',
    help: 'The share of visitors who get On. Raising it only adds people, so nobody who has On flips back to Off. Changing it by hand pauses a running plan.',
    getOn: 'get On',
    unsaved: 'Not saved',
    live: (percent: string) => `live: ${percent}`,
    capped: (holdback: string, max: string) => `The holdback keeps ${holdback} Off, so at most ${max} get On.`,
    // Where publishing puts a draft's rollout, where that is not the share typed here (publish-flag.ts).
    draftNotePlan: 'The plan sets the share when you publish.',
    draftNoteEditor: 'Left at 0%, publishing puts it on for everyone.',
    save: 'Save',
    discard: 'Discard',
    saved: 'Rollout saved.',
    invalid: 'Enter a share from 0 to 100.',
    planPausesNote: 'Saving pauses the running plan.',
    holdback: 'Holdback',
    holdbackNote: (percent: string) => `${percent} always Off`,
  },
  plan: {
    title: 'Rollout plan',
    description: 'Steps the rollout moves through on its own.',
    help: 'Each step sets the rollout to a share. A step moves on when its gate is met, and never before its Not before date. Pause the plan to change its steps.',
    empty: 'No plan: the rollout changes only by hand.',
    step: 'Step',
    share: 'Rollout %',
    stayAtLeast: 'Stay at least',
    stayAtLeastHelp: 'The least time the rollout stays at this step before it can move on.',
    notBefore: 'Not before',
    notBeforeHelp: 'The earliest date this step may move on, even once its gate is met. Leave it empty for no date.',
    gate: 'Moves on after',
    gateHelp:
      'Time: once the step has run its least time. Guardrail: once the guardrail finds On no worse than Off. Time and guardrail: both.',
    gates: {
      time: 'Time',
      kpi: 'Guardrail',
      both: 'Time and guardrail',
    } satisfies Record<FlagStepGate, string>,
    guardrailDecides: 'Until the guardrail clears',
    needsVisitorId: 'The guardrail needs the website visitor ID. Turn it on in the website settings to use it.',
    guardrailNeedsVisitorId: 'A guardrail step needs the website visitor ID.',
    margin: 'Guardrail margin %',
    marginTip:
      'How much worse On may convert than Off before the guardrail calls a breach and pauses the plan. A smaller margin is stricter.',
    marginHelp: (marginBp: number) =>
      `At ${Number((marginBp / 100).toFixed(2))}%, On must convert at least ${Number(((10_000 - marginBp) / 100).toFixed(2))}% as well as Off.`,
    marginInvalid: 'The margin is 0.01 to 50%.',
    hours: 'hours',
    days: 'days',
    add: 'Add step',
    remove: 'Remove step',
    save: 'Save plan',
    discard: 'Discard',
    saved: 'Plan saved.',
    start: 'Start plan',
    pause: 'Pause',
    resume: 'Resume',
    startTitle: 'Start the plan?',
    resumeTitle: 'Resume the plan?',
    // What Start or Resume does from the current rollout (resume-flag.ts).
    startsAt: (step: number, total: number, percent: string) =>
      `The plan picks up at step ${step} of ${total}, ${percent}.`,
    raises: (from: string, to: string) => `The rollout goes from ${from} to ${to} at once.`,
    holds: (percent: string) => `The rollout stays at ${percent}.`,
    stepTime: "That step's least time counts from now.",
    pastEvery: (percent: string) =>
      `The rollout is already at ${percent}, past every step. The plan is marked done and the rollout stays put.`,
    started: 'Plan started.',
    lockedActive: 'Pause the plan to change its steps.',
    statuses: {
      none: 'Not started',
      active: 'Running',
      paused: 'Paused',
      done: 'Done',
    },
    statusGuardrail: 'Paused by guardrail',
    holdback: 'Holdback %',
    holdbackTip:
      'A share kept Off at every step, even at 100%, so there is always a group to compare with. It can only be set before publishing.',
    holdbackInvalid: 'The holdback is 0 to 50%.',
  },
  guardrail: {
    title: 'Guardrail',
    description: 'Checks every five minutes that On does no worse than Off on the primary goal.',
    help: 'On a step that waits on it, the guardrail compares the visitors who got On and Off during the step. Clear or Pass lets the step move on, and a Breach pauses the plan.',
    verdicts: {
      collecting: 'Collecting',
      clear: 'Clear',
      pass: 'Pass',
      breach: 'Breach',
    } satisfies Record<FlagGuardrailVerdict, string>,
    explain: {
      collecting: `Not enough data yet. It needs ${FLAG_GUARDRAIL_MIN_VISITORS_PER_ARM.toLocaleString('en-US')} visitors a side and ${FLAG_GUARDRAIL_MIN_OFF_CONVERSIONS.toLocaleString('en-US')} Off conversions.`,
      clear: 'No harm detected, so the step will move on.',
      pass: 'On is proven no worse than Off, within the margin.',
      breach: 'On converts worse than Off, beyond the margin.',
    } satisfies Record<FlagGuardrailVerdict, string>,
    noCheck: 'No check of this step yet. The first comes within five minutes.',
    noCheckDraft: 'Checks start when the flag is published.',
    noCheckLabel: 'Waiting',
    side: 'Side',
    visitors: 'Visitors',
    conversions: 'Conversions',
    rate: 'Rate',
    on: 'On',
    off: 'Off',
    interval: (range: string) => `On vs Off: ${range}`,
    intervalPending: 'On vs Off: not enough visitors yet',
    margin: (percent: string) => `Margin ${percent}`,
    goal: 'Goal',
    checked: (ago: string) => `Checked ${ago} ago`,
    stepVisitors: 'Visitors new to the flag during this step.',
    limit: 'A guardrail step stops a large drop. A small one can still pass.',
    noVisitorId: 'The website visitor ID is off, so the guardrail cannot count visitors and stays at Collecting.',
  },
  goal: {
    title: 'Primary goal',
    description: 'The event the guardrail judges On and Off on.',
    help: 'The guardrail compares how often visitors with On and with Off send this event. It is fixed once the flag is live, so every step is judged on the same event.',
    noEvents: 'This website has not sent any events yet. Once it does, choose the goal here.',
    locked: 'Fixed while the flag is live.',
    saved: 'Goal saved.',
  },
  rings: {
    title: 'Rings',
    description: 'Groups that always get one side.',
    help: 'A ring gives a fixed side to a group, such as staff or a beta list, before the rollout applies. Rings are checked in order, and the first match wins.',
    empty: 'No rings.',
    ring: 'Ring',
    serve: 'Serve',
    ids: 'IDs',
    idsHelpCode: 'Visitor or customer IDs, one per line.',
    idsHelpEditor: 'Visitor IDs, one per line.',
    rulesLabel: 'Attribute rules',
    rulesHelpCode: 'Every rule must match the attributes your code passes.',
    rulesHelpEditor: 'Every rule must match window.PertentoAttributes on the page.',
    rulesEmpty: 'No rules.',
    rulesFull: 'A ring has at most 20 rules.',
    add: 'Add ring',
    remove: 'Remove ring',
    save: 'Save rings',
    discard: 'Discard',
    saved: 'Rings saved.',
  },
  editor: {
    title: 'Changes',
    description: 'What On changes on the page.',
    help: 'On applies the changes made in the visual editor, with the Pertento Editor extension. Off shows the page as it is.',
    open: 'Open site',
    how: 'In the Pertento Editor extension, pick this flag and its On variant.',
    openInEditor: 'Open in editor',
    openInEditorHow:
      "Opens the page with this flag's On variant picked in the Pertento Editor. If its panel stays closed, click the extension's icon.",
    install: 'Get the extension',
    changes: (count: number) => (count === 0 ? 'No changes yet' : count === 1 ? '1 change' : `${count} changes`),
  },
  code: {
    title: 'Use it in your code',
    description: 'Ask for the flag by its key, then show On or Off.',
    help: 'The calls for this flag, filled in with its key and website. Branch on On, and keep Off as what your code does today.',
    sdkTitle: 'SDK',
    sdkDescription:
      'Ask for the flag on your server, then show On or Off. Needs @pertentoai/sdk 0.2.0 or later. Keep the server key on the server.',
    browserTitle: 'Browser',
    browserDescription: 'Read the flag in your page code. Off on pages its targeting does not cover.',
    curlTitle: 'HTTP',
    curlDescription: 'Without the SDK, ask /sdk/decide directly.',
    guide: 'SDK guide',
    copyFailed: 'Could not copy. Select the code and copy it yourself.',
  },
  payloads: {
    title: 'Payloads',
    description: 'The JSON your code gets with each side.',
    help: 'Optional JSON that comes with the side a visitor gets, such as a colour or a limit. Set when the flag was created.',
    none: 'none',
  },
  targeting: {
    noteEditor: 'Only visitors on pages these rules match can get On. Everyone else sees the page as it is.',
    noteCode: 'In the browser, the flag is Off on pages these rules do not match. Your server uses the server rules.',
    liveNote: 'Changes here reach visitors when you publish them.',
    publish: 'Publish rules',
    published: 'Rules published.',
  },
  serverRules: {
    title: 'Server rules',
    description: 'Who your server can give On.',
    help: 'Attribute rules your server SDK checks before rings and the rollout. A visitor who does not match every rule gets Off.',
    label: 'Attribute rules',
    // The editor's own line under its label: the card's description already says who.
    helpLine: 'Every rule must match the attributes your code passes. Changes reach the SDK at once.',
    empty: 'No rules: everyone can get On.',
    full: 'A flag has at most 20 server rules.',
    dialogHelp: ['Every rule must match the attributes your code passes, or the visitor gets Off.'],
    saved: 'Server rules saved.',
  },
  activity: {
    title: 'Activity',
    description: 'Every change to this flag and its rollout.',
    help: 'Every publish, rollout change, plan step and turn off, with who made it. Changes the scheduler made carry no name.',
  },
  schedule: {
    title: 'Schedule',
    description: 'Publish or turn off at a set time.',
    help: 'Publish the flag, or turn it off, at a date and time you choose, without being there.',
    publishAt: 'Publish at',
    offAt: 'Turn off at',
    save: 'Save schedule',
    saved: 'Schedule saved.',
    discard: 'Discard',
    clear: 'Clear',
    incomplete: 'Finish the date and time, or press Clear to remove it.',
  },
  brief: {
    description: {
      title: 'Description & notes',
      description: 'What the flag does, and any notes for your team.',
      help: 'What this flag is for, in your words. The description written when the flag was created starts it.',
    },
    previews: {
      title: 'Previews',
      description: 'Screenshots of each side, from the visual editor.',
      help: 'Pictures of the page with Off and with On, captured in the visual editor.',
    },
  },
} as const;
